/**
 * NewLookupDialog — the inline "New {record}" quick-add opened from a lookup
 * field's green ＋ (prototype `openNewItem` / `NEW_FORMS`).
 *
 * Legacy parity with a deliberate simplification carried over from the
 * prototype (decision 2026-07-18): Client and Contact are QUICK-ADDs — only
 * what registration and the COC need.  The complete records (account
 * management, delivery flags, photos, child tabs …) stay in their master-data
 * screens, which the dialog says out loud.
 *
 * On OK the new row is POSTed to the quick-add API endpoint, then prepended to
 * the in-memory lookup and selected into the field that opened it.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDialog } from '../../../components/AppDialog';
import { LOOKUPS, NEWABLE, type SrLookupKey } from './srData';

/** One field in a quick-add form. */
interface QuickField {
  label: string;
  required?: boolean;
  kind?: 'text' | 'textarea' | 'radio';
  /** Radio choices (first is the default). */
  choices?: string[];
  placeholder?: string;
  readOnlyValue?: string;
  /** When set, the field is DISABLED and its value is COMPUTED from the other
   *  fields (e.g. the collector Full Name = First + Last) — matching ALPACA's
   *  read-only Full Name. It is never posted (the server derives it too). */
  derive?: (get: (label: string) => string) => string;
}

interface QuickForm {
  fields: QuickField[];
  width?: number;
  /** 2 = the prototype's wide two-column layout (Project only). */
  columns?: 1 | 2;
  footnote?: string;
  /** Build the lookup row (column order must match the lookup's `cols`). */
  toRow: (get: (label: string) => string) => string[];
}

function quickForm(kind: SrLookupKey, clientName: string): QuickForm {
  switch (kind) {
    case 'client':
      return {
        width: 520,
        footnote: 'Quick add — the full client record (account management, contacts, projects…) is managed in Client & Project ▸ Clients.',
        fields: [
          { label: 'Client', required: true },
          { label: 'Address 1', placeholder: 'Street address (shows on the COC)' },
          { label: 'Phone' },
          { label: 'Email' },
        ],
        toRow: g => [g('Client'), g('Address 1'), g('Phone')],
      };
    case 'contact':
      return {
        width: 520,
        footnote: 'Quick add — full contact details (address, delivery flags, photo…) are managed in Client & Project ▸ Contacts.',
        fields: [
          { label: 'First Name', required: true },
          { label: 'Last Name' },
          // The prototype's radio row carries NO label text.
          { label: '', kind: 'radio', choices: ['Report Contact', 'Invoice Contact'] },
          { label: 'Email' },
          { label: 'Mobile Phone' },
          { label: 'Client', readOnlyValue: clientName || 'N/A' },
        ],
        toRow: g => [[g('First Name'), g('Last Name')].filter(Boolean).join(' '), g('Email')],
      };
    case 'project':
      // The one WIDE quick-add — two columns, exactly the prototype's field set.
      return {
        width: 820,
        columns: 2,
        fields: [
          { label: 'Project ID', required: true },
          { label: 'Project Name' },
          { label: 'Client', readOnlyValue: clientName || 'N/A' },
          { label: 'Project Location', kind: 'textarea' },
          { label: 'Project City' },
          { label: 'To' },
          { label: 'CC' },
          { label: 'Project Overview' },
          { label: 'Project Source' },
          { label: 'Sample Retention' },
          { label: 'Comment', kind: 'textarea' },
        ],
        toRow: g => [
          g('Project ID'),
          g('Project Name') || g('Project ID'),
          g('Project Location').replace(/\n/g, ' '),
        ],
      };
    case 'collector':
      // Field ORDER matches ALPACA's Collector popup (user request 2026-10-05):
      // Client/Customer Name first, then Full Name (DISABLED — auto First + Last,
      // like ALPACA), First Name*, Last Name, Mobile Phone, Comment.
      return {
        width: 520,
        fields: [
          { label: 'Client Name', readOnlyValue: clientName || 'N/A' },
          { label: 'Full Name', derive: g => [g('First Name'), g('Last Name')].filter(Boolean).join(' ') },
          { label: 'First Name', required: true },
          { label: 'Last Name' },
          { label: 'Mobile Phone' },
          { label: 'Comment', kind: 'textarea' },
        ],
        toRow: g => [[g('First Name'), g('Last Name')].filter(Boolean).join(' ')],
      };
    case 'relinq':
      return {
        width: 520,
        fields: [
          { label: 'Relinquished By', required: true },
          { label: 'Relinquished Code' },
          { label: 'Comment', kind: 'textarea' },
        ],
        toRow: g => [g('Relinquished By')],
      };
    case 'category':
      return {
        width: 520,
        fields: [
          { label: 'Project Category', required: true },
          { label: 'Description' },
          { label: 'Non Commercial', kind: 'radio', choices: ['None', 'Yes'] },
        ],
        toRow: g => [g('Project Category')],
      };
    case 'inact':
      return {
        width: 520,
        fields: [
          { label: 'Action', required: true },
          { label: 'Inactivation Reason', required: true },
        ],
        toRow: g => [g('Action')],
      };
    default: {
      // Generic one-field-per-column dialog for the simple code lists.
      const cols = LOOKUPS[kind].cols;
      return {
        fields: cols.map((c, i) => ({ label: c, required: i === 0 })),
        toRow: g => cols.map(c => g(c)),
      };
    }
  }
}

export interface NewLookupDialogProps {
  /** Which lookup to add to; null = closed. */
  kind: SrLookupKey | null;
  /** The currently-selected client, shown read-only on the child forms. */
  clientName: string;
  /** The currently-selected client OID (passed to the API for FK linking). */
  clientOid?: string;
  onClose: () => void;
  /** The new row, ready to prepend to the lookup and select into the field. */
  onAdd: (kind: SrLookupKey, row: string[], oid?: string) => void;
  /** Shared warning (routes to the blocking modal — §18.6). */
  onWarn: (message: string) => void;
  /** Shared success notification. */
  onSuccess?: (message: string) => void;
}

export function NewLookupDialog({ kind, clientName, clientOid, onClose, onAdd, onWarn, onSuccess }: NewLookupDialogProps) {
  const form = useMemo(() => (kind ? quickForm(kind, clientName) : null), [kind, clientName]);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Reset the form each time it opens (radios default to their first choice).
  useEffect(() => {
    if (!form) return;
    const seed: Record<string, string> = {};
    for (const f of form.fields) {
      seed[f.label] = f.readOnlyValue ?? (f.kind === 'radio' ? (f.choices?.[0] ?? '') : '');
    }
    setVals(seed);
    setSaving(false);
  }, [form]);

  if (!kind || !form) return null;

  const get = (label: string) => (vals[label] ?? '').trim();
  const set = (label: string, v: string) => setVals(s => ({ ...s, [label]: v }));

  const submit = async () => {
    const missing = form.fields.find(f => f.required && !get(f.label));
    if (missing) { onWarn(`Enter the ${missing.label}.`); return; }

    const fields: Record<string, string> = {};
    for (const f of form.fields) {
      // Include every editable field, INCLUDING a radio with no visible label
      // (the Contact "Report Contact / Invoice Contact" row keys off "" on the
      // server) — otherwise the chosen type was dropped and always defaulted.
      // Skip DERIVED fields (collector Full Name) — they are computed, never typed,
      // and the server derives the same value from First + Last.
      if (!f.readOnlyValue && !f.derive && (f.label || f.kind === 'radio')) fields[f.label] = get(f.label);
    }

    setSaving(true);
    try {
      const res = await api.post('/api/v1/sample-registration/lookups/quick-add', {
        kind, fields, clientOid: clientOid || null,
      });
      const data = (res.data as { data?: { oid?: string; displayName?: string } }).data;
      onSuccess?.(`${NEWABLE[kind] ?? kind} created.`);
      onAdd(kind, form.toRow(get), data?.oid);
    } catch (err: unknown) {
      // Surface the server's real message (validation 400 / "Failed to create
      // lookup record." 500) instead of a blind generic — easier to diagnose.
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Failed to create the record. Try again.';
      onWarn(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open
      /* Prototype titles are the bare entity name, with no icon. */
      title={NEWABLE[kind] ?? kind}
      width={form.width ?? 460}
      onClose={onClose}
      footer={
        <>
          <button className="ca-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="ca-btn ca-btn-solid" onClick={submit} disabled={saving}>
            <span className="tb-i">💾</span> {saving ? 'Saving…' : 'OK'}
          </button>
        </>
      }
    >
      <div
        className="sr-fgrid"
        style={{
          gridTemplateColumns: form.columns === 2
            ? '150px minmax(0, 1fr) 150px minmax(0, 1fr)'   /* prototype n2col */
            : '150px minmax(0, 1fr)',
        }}
      >
        {form.fields.map(f => {
          // A derived field (collector Full Name) is DISABLED and shows the live
          // computed value; it recomputes on every render as First/Last change.
          const derived = f.derive ? f.derive(get) : undefined;
          return (
            <FieldRow
              key={f.label}
              field={f}
              value={derived !== undefined ? derived : (vals[f.label] ?? '')}
              disabled={derived !== undefined}
              onChange={v => set(f.label, v)}
            />
          );
        })}
      </div>
      {form.footnote && <div className="sr-hint-inline" style={{ marginTop: 8 }}>{form.footnote}</div>}
    </AppDialog>
  );
}

function FieldRow({ field, value, onChange, disabled }: {
  field: QuickField; value: string; onChange: (v: string) => void; disabled?: boolean;
}) {
  const ro = field.readOnlyValue !== undefined;
  return (
    <>
      <label className={field.required ? 'req' : undefined}>{field.label}</label>
      {field.kind === 'radio' ? (
        <span style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          {(field.choices ?? []).map(c => (
            <label key={c} style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 12.5, cursor: 'pointer', textAlign: 'left', fontWeight: 400 }}>
              <input type="radio" name={`sr-new-${field.label}`} checked={value === c}
                onChange={() => onChange(c)} />
              {c}
            </label>
          ))}
        </span>
      ) : field.kind === 'textarea' ? (
        <textarea rows={2} value={value} onChange={e => onChange(e.target.value)} />
      ) : (
        <input
          type="text"
          value={value}
          readOnly={ro}
          disabled={disabled}
          placeholder={field.placeholder}
          className={field.required && !value.trim() ? 'sr-req-empty' : undefined}
          onChange={e => onChange(e.target.value)}
        />
      )}
    </>
  );
}
